import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../storage/firebase';

export interface SessionData {
  sessionStart: Date;
  lastActivity: Date;
  totalSessionTime: number; // milliseconds
  accumulatedSessionTime: number; // milliseconds - time already saved to Firebase
  isActive: boolean;
}

class SessionTracker {
  private sessionData: SessionData | null = null;
  private updateInterval: NodeJS.Timeout | null = null;
  private activityCheckInterval: NodeJS.Timeout | null = null;
  private lastActivity: Date = new Date();
  private pageUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;
  private readonly SAVE_INTERVAL = 30 * 1000; // Save every 30 seconds
  private readonly INACTIVE_THRESHOLD = 5 * 60 * 1000; // 5 minutes of inactivity
  
  // Track user interactions to detect activity
  private readonly activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  // Start tracking session for a user
  startSession(userId: string): void {
    if (this.sessionData?.isActive) {
      console.log('Session already active, ignoring start request');
      return;
    }

    const now = new Date();
    this.sessionData = {
      sessionStart: now,
      lastActivity: now,
      totalSessionTime: 0,
      accumulatedSessionTime: 0,
      isActive: true
    };

    this.lastActivity = now;
    this.setupActivityListeners();
    this.startActivityCheck();
    this.setupPageUnloadHandler(userId);

    console.log('🎮 Session tracking started for user:', userId, 'at:', now.toISOString());
  }

  // End the current session
  async endSession(userId: string): Promise<void> {
    if (!this.sessionData?.isActive) {
      console.log('No active session to end');
      return;
    }

    // Calculate final session time
    const now = new Date();
    const sessionDuration = now.getTime() - this.sessionData.sessionStart.getTime();
    this.sessionData.totalSessionTime = sessionDuration;
    this.sessionData.isActive = false;

    // Save final session data
    await this.saveSessionData(userId);
    
    // Clean up
    this.cleanup();
    this.sessionData = null;

    console.log(`🏁 Session ended. Duration: ${Math.round(sessionDuration / 1000)} seconds`);
  }



  // Get total time played for user from Firebase
  async getTotalTimePlayed(userId: string): Promise<number> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.totalTimePlayed || 0;
      }
      return 0;
    } catch (error) {
      // Some roles (e.g. non-active teachers) intentionally cannot read/write analytics fields.
      // Treat permission-denied as a benign "0" rather than noisy console errors.
      const code = String((error as any)?.code || '').toLowerCase();
      const msg = String((error as any)?.message || '').toLowerCase();
      const isPermDenied = code.includes('permission-denied') || msg.includes('permission-denied');
      if (!isPermDenied) console.error('Error fetching total time played:', error);
      return 0;
    }
  }

  // Get current session time (for real-time display)
  getCurrentSessionTime(): number {
    if (!this.sessionData?.isActive) return 0;
    
    const now = new Date();
    const totalSessionTime = now.getTime() - this.sessionData.sessionStart.getTime();
    const currentSessionTime = totalSessionTime - this.sessionData.accumulatedSessionTime;
    
    // Debug logging
    console.log('🕐 Session Debug:', {
      isActive: this.sessionData?.isActive,
      sessionStart: this.sessionData?.sessionStart,
      now: now,
      totalSessionTime: Math.round(totalSessionTime / 1000),
      accumulatedTime: Math.round(this.sessionData.accumulatedSessionTime / 1000),
      currentSessionTime: Math.round(currentSessionTime / 1000),
      currentSessionTimeMs: currentSessionTime
    });
    
    return currentSessionTime;
  }

  // Get total time including current session (for real-time display)
  async getTotalTimeWithCurrentSession(userId: string): Promise<number> {
    const totalFromFirebase = await this.getTotalTimePlayed(userId);
    const currentSessionTime = this.getCurrentSessionTime();
    return totalFromFirebase + currentSessionTime;
  }

  // Format time in hours, minutes, seconds
  formatPlayTime(milliseconds: number): { hours: number; minutes: number; seconds: number; formatted: string } {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const formatted = hours > 0 
      ? `${hours}h ${minutes}m ${seconds}s`
      : minutes > 0 
      ? `${minutes}m ${seconds}s`
      : `${seconds}s`;

    return { hours, minutes, seconds, formatted };
  }

  // Setup activity event listeners
  private setupActivityListeners(): void {
    const updateActivity = () => {
      this.lastActivity = new Date();
    };

    this.activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
  }

  // Remove activity event listeners
  private removeActivityListeners(): void {
    const updateActivity = () => {
      this.lastActivity = new Date();
    };

    this.activityEvents.forEach(event => {
      document.removeEventListener(event, updateActivity);
    });
  }

  // Check for user inactivity
  private startActivityCheck(): void {
    this.activityCheckInterval = setInterval(() => {
      const now = new Date();
      const timeSinceActivity = now.getTime() - this.lastActivity.getTime();
      
      if (timeSinceActivity > this.INACTIVE_THRESHOLD && this.sessionData?.isActive) {
        console.log('⏰ User inactive, pausing session tracking');
        // Note: We don't end the session, just stop counting time
        // The session will resume when activity is detected
      }
    }, 60 * 1000); // Check every minute
  }

  // Save current session data to Firebase
  private async saveSessionData(userId: string): Promise<void> {
    if (!this.sessionData?.isActive) return;

    try {
      const now = new Date();
      const timeSinceActivity = now.getTime() - this.lastActivity.getTime();
      
      // Only count time if user was recently active
      if (timeSinceActivity <= this.INACTIVE_THRESHOLD) {
        const totalSessionTime = now.getTime() - this.sessionData.sessionStart.getTime();
        const unsavedSessionTime = totalSessionTime - this.sessionData.accumulatedSessionTime;
        
        if (unsavedSessionTime > 0) {
          // Get existing total time from Firebase
          const existingTotal = await this.getTotalTimePlayed(userId);
          
          // Calculate new total (existing + unsaved session time)
          const newTotal = existingTotal + unsavedSessionTime;
          
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            totalTimePlayed: newTotal,
            lastActive: now,
            lastSessionStart: this.sessionData.sessionStart
          });

          // Update accumulated session time to avoid double-counting
          this.sessionData.accumulatedSessionTime = totalSessionTime;
          
          console.log(`💾 Session data saved. Unsaved session: ${Math.round(unsavedSessionTime / 1000)}s, Total: ${Math.round(newTotal / 1000)}s`);
        }
      }
    } catch (error) {
      const code = String((error as any)?.code || '').toLowerCase();
      const msg = String((error as any)?.message || '').toLowerCase();
      const isPermDenied = code.includes('permission-denied') || msg.includes('permission-denied');
      if (!isPermDenied) console.error('Error saving session data:', error);
    }
  }

  // Clean up intervals and listeners
  private cleanup(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
    
    this.removeActivityListeners();
    this.removePageUnloadHandler();
  }

  // Setup page unload handler to save session data
  private setupPageUnloadHandler(userId: string): void {
    const handlePageUnload = async (event: BeforeUnloadEvent) => {
      if (this.sessionData?.isActive) {
        console.log('🔄 Page unloading, saving session data...');
        
        // Calculate current session time
        const now = new Date();
        const currentSessionTime = now.getTime() - this.sessionData.sessionStart.getTime();
        
        // Save to localStorage as backup (since Firebase might not complete)
        const backupData = {
          userId,
          sessionTime: currentSessionTime,
          timestamp: now.getTime()
        };
        localStorage.setItem('sessionBackup', JSON.stringify(backupData));
        
        // Try to save to Firebase (might not complete due to page unload)
        try {
          const existingTotal = await this.getTotalTimePlayed(userId);
          const newTotal = existingTotal + currentSessionTime;
          
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            totalTimePlayed: newTotal,
            lastActive: now
          });
          
          console.log('✅ Session data saved before page unload');
        } catch (error) {
          const code = String((error as any)?.code || '').toLowerCase();
          const msg = String((error as any)?.message || '').toLowerCase();
          const isPermDenied = code.includes('permission-denied') || msg.includes('permission-denied');
          if (!isPermDenied) console.error('❌ Error saving session data on unload:', error);
        }
      }
    };

    // Store the handler so we can remove it later
    this.pageUnloadHandler = handlePageUnload;
    window.addEventListener('beforeunload', handlePageUnload);
  }

  // Remove page unload handler
  private removePageUnloadHandler(): void {
    if (this.pageUnloadHandler) {
      window.removeEventListener('beforeunload', this.pageUnloadHandler);
      this.pageUnloadHandler = null;
    }
  }

  // Check for and restore session data from localStorage on page load
  async checkForSessionBackup(currentUserId?: string | null): Promise<void> {
    const backupData = localStorage.getItem('sessionBackup');
    if (backupData) {
      try {
        const backup = JSON.parse(backupData);
        const expectedUid = String(currentUserId || '').trim();
        const backupUid = String(backup?.userId || '').trim();

        // Prevent cross-account writes: if the backup belongs to a different user
        // than the currently signed-in user, discard it.
        if (expectedUid && backupUid && expectedUid !== backupUid) {
          localStorage.removeItem('sessionBackup');
          return;
        }

        const now = Date.now();
        const timeSinceBackup = now - backup.timestamp;
        
        // Only restore if backup is recent (within last 5 minutes)
        if (timeSinceBackup < 5 * 60 * 1000) {
          console.log('🔄 Found session backup, restoring...');
          
          try {
            // Add the backed up session time to Firebase
            const existingTotal = await this.getTotalTimePlayed(backup.userId);
            const newTotal = existingTotal + backup.sessionTime;
            
            const userRef = doc(db, 'users', backup.userId);
            await updateDoc(userRef, {
              totalTimePlayed: newTotal,
              lastActive: new Date(backup.timestamp)
            });
            
            console.log(`✅ Restored ${Math.round(backup.sessionTime / 1000)}s of session time`);
          } catch (error) {
            const code = String((error as any)?.code || '').toLowerCase();
            const msg = String((error as any)?.message || '').toLowerCase();
            const isPermDenied = code.includes('permission-denied') || msg.includes('permission-denied');
            if (!isPermDenied) console.error('Error restoring session backup:', error);
          }
        }
        
        // Clear the backup
        localStorage.removeItem('sessionBackup');
      } catch (error) {
        console.error('Error restoring session backup:', error);
        localStorage.removeItem('sessionBackup');
      }
    }
  }

  // Get session status
  isSessionActive(): boolean {
    return this.sessionData?.isActive || false;
  }
}

// Export singleton instance
export const sessionTracker = new SessionTracker(); 